import { Injectable } from '@angular/core';
import { LocalStorageService } from 'src/app/services/storage/storage.service';
import { environment } from 'src/environments/environment';

/**
 * Feature availability and year-scoped browser progress, cleared on signout.
 * Only flags and a slide index are stored locally; no story content or health values.
 */
const SLEEP_STORY_ROUTE = '/sleep-story';
export const SLEEP_STORY_YEAR = 2026;
/** Pending direct links expire after this long. */
export const SLEEP_STORY_DEEP_LINK_TTL_MS = 60 * 60 * 1000;
const PENDING_DEEP_LINK_KEY = 'SLEEP_STORY_PENDING_DEEP_LINK';
const DEEP_LINK_PATTERN = /^\/sleep-story\/?$/;

@Injectable({
  providedIn: 'root',
})
export class SleepStoryStateService {
  constructor(private readonly localStorageService: LocalStorageService) {}

  get isEnabled(): boolean {
    return environment.sleepStoryEnabled;
  }

  get usesMockData(): boolean {
    return !environment.production;
  }

  get route(): string[] {
    return [SLEEP_STORY_ROUTE];
  }

  hasSeenReadyPrompt(): boolean {
    return this.getFlag('ready_prompt_seen');
  }

  markReadyPromptSeen(): void {
    this.setFlag('ready_prompt_seen');
  }

  isCompleted(): boolean {
    return this.getFlag('completed');
  }

  markCompleted(): void {
    this.setFlag('completed');
    this.localStorageService.removeItem(this.key('resume_index'));
  }

  getResumeIndex(): number {
    const index = Number(this.localStorageService.getItem(this.key('resume_index')));
    return Number.isInteger(index) && index > 0 ? index : 0;
  }

  setResumeIndex(index: number): void {
    this.localStorageService.setItem(this.key('resume_index'), String(index));
  }

  setPendingDeepLink(url: string, now = Date.now()): void {
    const path = url.split(/[?#]/)[0];
    if (DEEP_LINK_PATTERN.test(path)) {
      this.localStorageService.setItem(
        PENDING_DEEP_LINK_KEY,
        JSON.stringify({ path: SLEEP_STORY_ROUTE, at: now }),
      );
    }
  }

  clearPendingDeepLink(): void {
    this.localStorageService.removeItem(PENDING_DEEP_LINK_KEY);
  }

  /** Returns and clears the pending direct link, if one exists and hasn't expired. */
  consumePendingDeepLink(now = Date.now()): string | null {
    const raw = this.localStorageService.getItem(PENDING_DEEP_LINK_KEY);
    if (!raw) {
      return null;
    }
    this.clearPendingDeepLink();
    try {
      const { path, at } = JSON.parse(raw) as { path?: unknown; at?: unknown };
      const fresh = typeof at === 'number' && now - at >= 0 && now - at < SLEEP_STORY_DEEP_LINK_TTL_MS;
      return fresh && typeof path === 'string' && DEEP_LINK_PATTERN.test(path) ? path : null;
    } catch {
      return null;
    }
  }

  private getFlag(name: string): boolean {
    return this.localStorageService.getItem(this.key(name)) === 'true';
  }

  private setFlag(name: string): void {
    this.localStorageService.setItem(this.key(name), 'true');
  }

  private key(name: string): string {
    return `SLEEP_STORY_${SLEEP_STORY_YEAR}_${name}`.toUpperCase();
  }
}
